import { Injectable, inject } from '@angular/core';
import { Router, NavigationStart, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { filter } from 'rxjs/operators';
import { StateSnapshotService } from './state-snapshot.service';
import { shouldPreserveRoute, findRouteConfig } from '../config/route-state.config';
import { NavigationStateService } from '@core/navigation-state';

/**
 * Navigation Interceptor Service
 *
 * Automatically captures and restores component state during navigation.
 * This service runs in the background and requires no component interaction.
 *
 * Lifecycle:
 * 1. NavigationStart → Capture state of current route (if configured)
 * 2. NavigationEnd → Restore state of new route (if exists)
 * 3. NavigationCancel/Error → No action
 */
@Injectable({
  providedIn: 'root'
})
export class NavigationInterceptorService {
  private router = inject(Router);
  private stateSnapshot = inject(StateSnapshotService);
  private navigationStateService = inject(NavigationStateService);

  // Track current route for state capture
  private currentRoute: string | null = null;
  private previousRoute: string | null = null;

  // Track navigation in progress
  private navigationInProgress = false;

  constructor() {
    this.initializeInterceptor();
  }

  /**
   * Initialize the navigation interceptor
   */
  private initializeInterceptor(): void {
    this.setupNavigationStartHandler();
    this.setupNavigationEndHandler();
    this.setupNavigationCancelHandler();
    this.setupNavigationErrorHandler();

    console.log('[NavigationInterceptor] Initialized - State preservation active');
  }

  /**
   * Handle NavigationStart - Capture state before leaving
   */
  private setupNavigationStartHandler(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        this.navigationInProgress = true;
        const currentUrl = this.router.url;
        const routePath = this.extractRoutePath(currentUrl);

        // Store current route for later reference
        this.previousRoute = this.currentRoute;
        this.currentRoute = routePath;

        // Capture state if route is configured for preservation
        if (shouldPreserveRoute(routePath)) {
          const config = findRouteConfig(routePath);
          console.log(`[NavigationInterceptor] Leaving route: ${routePath} - Capturing state`);

          // Capture state before navigation
          this.stateSnapshot.captureState(routePath, {
            includeScrollPosition: config?.preserveScrollPosition !== false
          });
        }
      });
  }

  /**
   * Handle NavigationEnd - Restore state after arriving
   */
  private setupNavigationEndHandler(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.navigationInProgress = false;
        const newUrl = event.urlAfterRedirects || event.url;
        const routePath = this.extractRoutePath(newUrl);

        // Update current route
        this.currentRoute = routePath;

        // Check if we should restore state for this route
        if (shouldPreserveRoute(routePath)) {
          const hasState = this.stateSnapshot.hasState(routePath);

          if (hasState) {
            // RouteReuseStrategy reattached the live component tree for this navigation —
            // the component already has perfect state. Skip serialised restore entirely.
            if (this.navigationStateService.wasLastNavigationAReattach()) {
              console.log(`[NavigationInterceptor] Skipping restore for "${routePath}" — RouteReuseStrategy reattached this component`);
              return;
            }

            console.log(`[NavigationInterceptor] Arrived at route: ${routePath} - Restoring state`);

            const config = findRouteConfig(routePath);

            // Restore state after a short delay to allow component initialization
            setTimeout(() => {
              this.stateSnapshot.restoreState(routePath, {
                restoreScrollPosition: config?.preserveScrollPosition !== false,
                scrollRestoreDelay: 150
              });
            }, 50);
          } else {
            console.log(`[NavigationInterceptor] Arrived at route: ${routePath} - No stored state (fresh load)`);
          }
        }
      });
  }

  /**
   * Handle NavigationCancel - Clean up
   */
  private setupNavigationCancelHandler(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationCancel))
      .subscribe(() => {
        this.navigationInProgress = false;
        console.log('[NavigationInterceptor] Navigation cancelled');
      });
  }

  /**
   * Handle NavigationError - Clean up
   */
  private setupNavigationErrorHandler(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationError))
      .subscribe((event: NavigationError) => {
        this.navigationInProgress = false;
        console.error('[NavigationInterceptor] Navigation error:', event.error);
      });
  }

  /**
   * Extract route path from URL (removes query params and fragments)
   */
  private extractRoutePath(url: string): string {
    // Remove query parameters
    let path = url.split('?')[0];

    // Remove fragment
    path = path.split('#')[0];

    // Remove leading slash if present
    if (path.startsWith('/')) {
      path = path.substring(1);
    }

    return path || '/';
  }

  /**
   * Get current route path
   */
  getCurrentRoute(): string | null {
    return this.currentRoute;
  }

  /**
   * Get previous route path
   */
  getPreviousRoute(): string | null {
    return this.previousRoute;
  }

  /**
   * Check if navigation is in progress
   */
  isNavigating(): boolean {
    return this.navigationInProgress;
  }

  /**
   * Manually trigger state capture for current route
   */
  captureCurrentState(): void {
    if (this.currentRoute) {
      this.stateSnapshot.captureState(this.currentRoute, { force: true });
    }
  }

  /**
   * Manually clear state for a route
   */
  clearRouteState(routePath: string): void {
    this.stateSnapshot.clearState(routePath);
  }

  /**
   * Clear all stored states
   */
  clearAllStates(): void {
    this.stateSnapshot.clearAllStates();
  }
}
